// Zustand store for the active-case context — the add-in's 2nd store
// (after useAuthStore). Reads ports from the composition root.
//
// W5 (single-writer): user-scoped → exposes reset(), wired into
// useAuth.logout(), AND hydrate-on-entry via initialize() called once from
// App. A 2nd store doubles the cross-user-leak surface; the reset wiring +
// the relogin-empty test are the load-bearing guards.
//
// XmlPart-first precedence: initialize() reads the doc's XmlPart anchor. A
// doc already bound to a case/brief is the source of truth; the picker is
// only the source for fresh (unbound) docs. The XmlPart is mounted lazily
// on first cite (PR-B), never on pick.

import { create } from 'zustand'
import { getContainer } from '@/container'
import type {
  CaseSummary,
  BriefSummary,
  ActiveCaseContext,
} from '@/domain/case/entity'

/** Citation style profile default. Named honestly as a placeholder: the
 *  real value would come from `/styles/resolved`, which returns a cascade
 *  label (not an id) — resolving it is deferred to the editor/style
 *  feature (plan §5). The anchor's styleProfile does NOT drive citation
 *  rendering today. */
export const STYLE_PROFILE_PLACEHOLDER = 'fr-fr-nf-z44-005'

interface CaseState {
  cases: CaseSummary[]
  briefs: BriefSummary[]
  selectedCaseId: string | null
  activeContext: ActiveCaseContext | null
  loading: boolean
  error: Error | null
  /** Hydrate from the doc's XmlPart anchor (XmlPart-first). Called once
   *  from App on mount. No-op (null context) on a fresh/unbound doc or
   *  when running outside the Word host. */
  initialize: () => Promise<void>
  /** Load the picker's case list. */
  loadCases: () => Promise<void>
  /** Pick a case → load its briefs (does not set activeContext yet). */
  selectCase: (caseId: string) => Promise<void>
  /** Pick a brief → bind the active context (caseId + the 4 anchor fields). */
  selectBrief: (briefId: string) => void
  /** W5 — clear all user-scoped state. Called on logout. */
  reset: () => void
}

const EMPTY = {
  cases: [] as CaseSummary[],
  briefs: [] as BriefSummary[],
  selectedCaseId: null,
  activeContext: null,
  loading: false,
  error: null,
}

export const useCaseStore = create<CaseState>((set, get) => ({
  ...EMPTY,

  initialize: async () => {
    try {
      const meta = await getContainer().documentMetadata.read()
      // Only hydrate a doc that is actually bound (non-empty briefId).
      if (meta && meta.briefId) {
        set({
          activeContext: {
            caseId: meta.caseId,
            briefId: meta.briefId,
            snapshotVersion: meta.snapshotVersion,
            lang: meta.lang,
            styleProfile: meta.styleProfile,
          },
        })
      }
    } catch {
      // xml-part-missing (fresh doc) OR sync-failed (outside Word host) →
      // no hydration. The picker is the source for fresh docs.
    }
  },

  loadCases: async () => {
    set({ loading: true, error: null })
    try {
      const cases = await getContainer().caseList.listCases()
      set({ cases, loading: false })
    } catch (err: unknown) {
      set({
        loading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      })
    }
  },

  selectCase: async (caseId: string) => {
    set({ selectedCaseId: caseId, briefs: [], loading: true, error: null })
    try {
      const briefs = await getContainer().caseList.listBriefs(caseId)
      set({ briefs, loading: false })
    } catch (err: unknown) {
      set({
        loading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      })
    }
  },

  selectBrief: (briefId: string) => {
    const { briefs, selectedCaseId } = get()
    const brief = briefs.find((b) => b.id === briefId)
    if (!brief || selectedCaseId === null) return
    set({
      activeContext: {
        caseId: selectedCaseId,
        briefId: brief.id,
        snapshotVersion: brief.version,
        lang: brief.language,
        styleProfile: STYLE_PROFILE_PLACEHOLDER,
      },
    })
  },

  reset: () => set({ ...EMPTY }),
}))
